// MARK://dhk8/pos/pages/PettyCashDetailPage
// Detalle de un retiro petty-cash — ruta /cashout/retiros/:id. FASE 2 del ciclo
// 2-fases (ver CashoutPage — FASE 1 — y ReconcileForm):
//
//   OPEN        → muestra `ReconcileForm` (líneas + soportes + cambio). Al
//                 conciliar: (a) el propio ReconcileForm sube los soportes
//                 (uploadMedia) y entrega el payload ya armado; (b) si hay
//                 cambio (changeCents>0) esta página crea el CASH_PICKUP del
//                 cambio ANTES de llamar a reconcile; (c) llama
//                 `reconcilePettyCashPurchase` con un idempotencyKey ESTABLE
//                 (generado una vez, no se regenera entre reintentos).
//                 También ofrece "Cancelar retiro" (motivo obligatorio) →
//                 `cancelPettyCashPurchase` + un CASH_PICKUP de reintegro por
//                 el efectivo retirado (el backend NO reintegra solo — "aquí
//                 sólo marcamos", ver gastos.repository.ts#cancelarCompra).
//   RECONCILED /
//   CANCELLED   → vista de sólo lectura: resumen, líneas (con su estado de
//                 materialización vía stockLotId/expenseId) y soportes.
//
// Money-path: cancelar y "cambio de vuelta" mueven efectivo real (CASH_PICKUP),
// así que ambos piden autorización de gerente (requireManagerAuth) igual que
// el resto de ingresos de caja en CashoutPage — decisión tomada aquí porque la
// conciliación puede ocurrir fuera del flujo gateado original del CASH_DROP.

import { useCallback, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { ArrowLeft, Ban } from 'lucide-react'

import {
  getPettyCashPurchase, reconcilePettyCashPurchase, cancelPettyCashPurchase,
  PETTY_CASH_CHANGE_REASON, PETTY_CASH_REFUND_REASON,
  type PettyCashPurchaseResult,
} from '../lib/clients/petty-cash.js'
import { useCashcutStore } from '../stores/cashcut-store.js'
import { useAuthStore } from '../stores/auth-store.js'
import { requireManagerAuth } from '../features/security/pin-gate-store.js'
import { formatCents } from '../lib/money.js'
import { StatusChip, type ChipTone } from '../components/FilterableTable.js'
import { ReconcileForm, type ReconcileSubmitPayload } from '../features/petty-cash/ReconcileForm.js'

function uuid(): string {
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto
  return c?.randomUUID ? c.randomUUID() : `xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx`.replace(/[xy]/g, (ch) => {
    const r = (Math.random() * 16) | 0
    return (ch === 'x' ? r : (r & 0x3) | 0x8).toString(16)
  })
}

const STATUS_LABEL: Record<string, string> = {
  OPEN: '⏳ Pendiente de conciliar',
  RECONCILED: '✓ Conciliado',
  CANCELLED: 'Cancelado',
}
const STATUS_TONE: Record<string, ChipTone> = { OPEN: 'warning', RECONCILED: 'success', CANCELLED: 'neutral' }

function fmtDateTimeMx(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return new Intl.DateTimeFormat('es-MX', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }).format(d)
}

export default function PettyCashDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const recordCut = useCashcutStore((s) => s.recordCut)
  const cashierId = useAuthStore((s) => s.user?.id ?? null)

  const detailQuery = useQuery({
    queryKey: ['petty-cash', 'detail', id],
    queryFn: () => getPettyCashPurchase(id ?? ''),
    enabled: Boolean(id),
  })
  const purchase = detailQuery.data ?? null

  // idempotencyKey del reconcile: ESTABLE entre reintentos (no se regenera si el
  // submit falla y el cajero vuelve a intentar) — se crea una sola vez por compra.
  const [idempotencyKey] = useState<string>(() => uuid())
  // cashPickupId del cambio: creado una sola vez aunque se reintente el submit.
  const [changePickupId, setChangePickupId] = useState<string | null>(null)
  const [reconcileError, setReconcileError] = useState<string | null>(null)
  const [pickupBusy, setPickupBusy] = useState(false)

  const [cancelBusy, setCancelBusy] = useState(false)
  const [cancelError, setCancelError] = useState<string | null>(null)

  const refetch = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['petty-cash', 'detail', id] })
    void queryClient.invalidateQueries({ queryKey: ['petty-cash', 'list'] })
  }, [queryClient, id])

  // ── FASE 2a: conciliar (ReconcileForm ya subió los soportes) ──────────────
  const handleReconcile = useCallback(async (payload: ReconcileSubmitPayload): Promise<void> => {
    if (!purchase) return
    setReconcileError(null)
    try {
      // (b) crea el CASH_PICKUP del cambio ANTES de reconcile — una sola vez.
      let cashPickupId = changePickupId
      if (payload.changeCents > 0 && !cashPickupId) {
        setPickupBusy(true)
        try {
          const pickup = await recordCut({
            kind: 'CASH_PICKUP',
            reason: PETTY_CASH_CHANGE_REASON,
            purchaseRef: purchase.id,
            countedCents: payload.changeCents,
            expectedCents: payload.changeCents,
            notes: `Cambio de compra${payload.supplierId ? '' : ''} — retiro ${formatCents(purchase.withdrawnCents)}`,
          })
          cashPickupId = pickup.id
          setChangePickupId(cashPickupId)
        } finally {
          setPickupBusy(false)
        }
      }
      // (c) reconcile — idempotencyKey estable, no se regenera entre reintentos.
      await reconcilePettyCashPurchase(purchase.id, {
        lines: payload.lines,
        changeCents: payload.changeCents,
        cashierContributedCents: payload.cashierContributedCents,
        ...(cashPickupId ? { cashPickupId } : {}),
        ...(payload.supplierId ? { supplierId: payload.supplierId } : {}),
        ...(payload.notes ? { notes: payload.notes } : {}),
        ...(cashierId ? { reconciledByCashierId: cashierId } : {}),
        attachments: payload.attachments,
        idempotencyKey,
      })
      refetch()
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'No se pudo conciliar la compra'
      setReconcileError(msg)
      // Re-lanza (con cause) para que ReconcileForm también refleje el error inline.
      throw new Error(msg, { cause: e })
    }
  }, [purchase, changePickupId, cashierId, idempotencyKey, recordCut, refetch])

  // ── Cancelar el retiro (sólo mientras OPEN) ───────────────────────────────
  async function handleCancel(): Promise<void> {
    if (!purchase) return
    const auth = await requireManagerAuth('cash.pickup', {
      requireReason: true,
      contextLabel: `Cancelar retiro ${formatCents(purchase.withdrawnCents)} — reintegra el efectivo al cajón`,
    })
    if (!auth.proceed) return
    const reason = (auth.reason ?? '').trim() || 'Retiro cancelado por gerente'
    setCancelBusy(true); setCancelError(null)
    try {
      // cancelarCompra es idempotente (compare-and-swap sobre status='OPEN') —
      // seguro reintentar si el reintegro de abajo falla y el cajero reintenta.
      await cancelPettyCashPurchase(purchase.id, reason)
      await recordCut({
        kind: 'CASH_PICKUP',
        reason: PETTY_CASH_REFUND_REASON,
        purchaseRef: purchase.id,
        countedCents: purchase.withdrawnCents,
        expectedCents: purchase.withdrawnCents,
        notes: `Reintegro por cancelación: ${reason}`,
      })
      refetch()
    } catch (e) {
      setCancelError(e instanceof Error ? e.message : 'No se pudo cancelar el retiro')
    } finally {
      setCancelBusy(false)
    }
  }

  return (
    <div className="dhk8-pcd-root">
      <header className="dhk8-pcd-head">
        <button type="button" className="dhk8-pcd-back" onClick={() => navigate('/cashout/retiros')} aria-label="Volver a retiros">
          <ArrowLeft size={18} />
        </button>
        <div>
          <div className="dhk8-tk-eyebrow">Caja · Petty-cash</div>
          <h1 className="dhk8-tk-title">Retiro {purchase ? formatCents(purchase.withdrawnCents) : ''}</h1>
        </div>
        {purchase && <StatusChip tone={STATUS_TONE[purchase.status] ?? 'neutral'}>{STATUS_LABEL[purchase.status] ?? purchase.status}</StatusChip>}
      </header>

      {detailQuery.isLoading && <p className="dhk8-cashcut-hint">Cargando…</p>}
      {detailQuery.isError && <div className="dhk8-cashcut-error">No se pudo cargar el retiro.</div>}

      {purchase && (
        <>
          <div className="dhk8-pcd-summary">
            <span>Retirado <strong>{formatCents(purchase.withdrawnCents)}</strong></span>
            <span>Abierto <strong>{fmtDateTimeMx(purchase.createdAt)}</strong></span>
            {purchase.notes && <span>Notas <strong>{purchase.notes}</strong></span>}
          </div>

          {purchase.status === 'OPEN' && (
            <>
              <ReconcileForm
                withdrawnCents={purchase.withdrawnCents}
                onSubmit={handleReconcile}
                disabled={pickupBusy}
              />
              {reconcileError && <div className="dhk8-cashcut-error">{reconcileError}</div>}

              <div className="dhk8-pcd-cancel">
                <button type="button" className="dhk8-pcd-cancel-btn" onClick={() => void handleCancel()} disabled={cancelBusy}>
                  <Ban size={16} /> {cancelBusy ? 'Cancelando…' : 'Cancelar retiro'}
                </button>
                {cancelError && <div className="dhk8-cashcut-error">{cancelError}</div>}
              </div>
            </>
          )}

          {purchase.status !== 'OPEN' && <ReadOnlyPurchase purchase={purchase} />}
        </>
      )}

      <style>{styles}</style>
    </div>
  )
}

// ── Vista de sólo lectura (RECONCILED / CANCELLED) ───────────────────────────

function ReadOnlyPurchase({ purchase }: { purchase: PettyCashPurchaseResult }) {
  return (
    <div className="dhk8-pcd-readonly">
      <div className="dhk8-pcd-money">
        <span>Retirado <strong>{formatCents(purchase.withdrawnCents)}</strong></span>
        <span>Gastado <strong>{formatCents(purchase.spentCents)}</strong></span>
        <span>Cambio <strong>{formatCents(purchase.changeCents)}</strong></span>
        {purchase.cashierContributedCents > 0 && <span>Aporte cajero <strong>{formatCents(purchase.cashierContributedCents)}</strong></span>}
        {purchase.reconciledAt && <span>Conciliado <strong>{fmtDateTimeMx(purchase.reconciledAt)}</strong></span>}
        {purchase.cancelledAt && <span>Cancelado <strong>{fmtDateTimeMx(purchase.cancelledAt)}</strong></span>}
        {purchase.cancelledReason && <span>Motivo <strong>{purchase.cancelledReason}</strong></span>}
      </div>

      {purchase.lines.length > 0 && (
        <table className="dhk8-pcd-table">
          <thead>
            <tr>
              <th>Línea</th><th>Cant.</th><th>Costo unit.</th><th>Total</th><th>Destino</th><th>Materializado</th>
            </tr>
          </thead>
          <tbody>
            {purchase.lines.map((l) => (
              <tr key={l.id}>
                <td>{l.label}</td>
                <td>{l.qty}</td>
                <td>{formatCents(l.unitCostCents)}</td>
                <td>{formatCents(l.totalCents)}</td>
                <td>{l.capitalizable ? 'Inventario' : 'Gasto'}</td>
                <td>
                  {l.stockLotId ? `✓ StockLot ${l.stockLotId.slice(0, 8)}…`
                    : l.expenseId ? `✓ Gasto ${l.expenseId.slice(0, 8)}…`
                    : '— sin materializar'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {(purchase.attachments?.length ?? 0) > 0 && (
        <div className="dhk8-pcd-attachments">
          <span className="dhk8-pcd-attachments__title">Soportes</span>
          <ul>
            {purchase.attachments?.map((a) => (
              <li key={a.id}>{a.label ?? a.storageKey} <em>({a.contentType})</em></li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

const styles = `
  .dhk8-pcd-root { display:flex; flex-direction:column; gap:var(--space-4); min-height:0; flex:1; overflow-y:auto; -webkit-overflow-scrolling:touch; padding-bottom:var(--space-4); }
  .dhk8-pcd-head { display:flex; align-items:center; gap:var(--space-3); }
  .dhk8-pcd-back { width:44px; height:44px; display:flex; align-items:center; justify-content:center; border:1px solid var(--separator-opaque); background:var(--bg-surface); color:var(--text-primary); cursor:pointer; border-radius:var(--radius-sm); flex-shrink:0; }
  .dhk8-pcd-back:hover { background:var(--bg-elevated); }
  .dhk8-tk-eyebrow { font-family:var(--font-mono); font-size:var(--text-xxs); letter-spacing:.12em; text-transform:uppercase; color:var(--text-tertiary); }
  .dhk8-tk-title { font-family:var(--font-display); font-size:var(--text-xl); font-weight:600; color:var(--text-primary); margin:var(--space-1) 0 0; }

  .dhk8-pcd-summary { display:flex; flex-wrap:wrap; gap:var(--space-2) var(--space-4); padding:var(--space-3); border:1px solid var(--separator-opaque); border-radius:var(--radius-md); background:var(--bg-surface); font-family:var(--font-mono); font-size:var(--text-sm); color:var(--text-secondary); }
  .dhk8-pcd-summary strong { color:var(--text-primary); font-variant-numeric:tabular-nums; }

  .dhk8-pcd-cancel { display:flex; flex-direction:column; gap:var(--space-2); align-items:flex-start; }
  .dhk8-pcd-cancel-btn { display:inline-flex; align-items:center; gap:var(--space-2); min-height:44px; padding:0 var(--space-4); border:1px solid var(--pos-danger,#E5484D); color:var(--pos-danger,#E5484D); background:transparent; font-family:var(--font-mono); font-size:var(--text-sm); cursor:pointer; border-radius:var(--radius-sm); }
  .dhk8-pcd-cancel-btn:hover:not(:disabled) { background:color-mix(in oklch, var(--pos-danger,#E5484D) 12%, transparent); }
  .dhk8-pcd-cancel-btn:disabled { opacity:.5; cursor:not-allowed; }

  .dhk8-pcd-readonly { display:flex; flex-direction:column; gap:var(--space-3); }
  .dhk8-pcd-money { display:flex; flex-wrap:wrap; gap:var(--space-2) var(--space-4); padding:var(--space-3); border:1px solid var(--separator); border-radius:var(--radius-sm); background:var(--bg-surface); font-family:var(--font-mono); font-size:var(--text-sm); color:var(--text-secondary); }
  .dhk8-pcd-money strong { color:var(--text-primary); font-variant-numeric:tabular-nums; }

  .dhk8-pcd-table { width:100%; border-collapse:collapse; font-family:var(--font-mono); font-size:var(--text-xs); font-variant-numeric:tabular-nums; }
  .dhk8-pcd-table th,.dhk8-pcd-table td { padding:var(--space-2); text-align:left; border-bottom:1px solid var(--separator); }
  .dhk8-pcd-table th { color:var(--text-tertiary); font-size:var(--text-xxs); letter-spacing:.06em; text-transform:uppercase; }

  .dhk8-pcd-attachments { display:flex; flex-direction:column; gap:var(--space-1); padding:var(--space-3); border:1px solid var(--separator); border-radius:var(--radius-sm); background:var(--bg-surface); }
  .dhk8-pcd-attachments__title { font-family:var(--font-mono); font-size:var(--text-xxs); letter-spacing:.08em; text-transform:uppercase; color:var(--text-tertiary); }
  .dhk8-pcd-attachments ul { list-style:none; margin:0; padding:0; display:flex; flex-direction:column; gap:4px; font-family:var(--font-mono); font-size:var(--text-xs); color:var(--text-secondary); }
  .dhk8-pcd-attachments em { font-style:normal; color:var(--text-tertiary); }

  .dhk8-cashcut-hint { margin:0; font-family:var(--font-mono); font-size:var(--text-xxs); color:var(--text-tertiary); }
  .dhk8-cashcut-error { padding:var(--space-2) var(--space-3); background:var(--status-error-bg); border:1px solid var(--status-error); color:var(--status-error); font-family:var(--font-mono); font-size:var(--text-xs); border-radius:var(--radius-sm); }
`
